<!-- Batch Scan Station.
     Left: controls + scan inputs (camera + keyboard-wedge). Right: live activity feed.
     Both inputs feed the same feed; each scan is pushed to the backend independently. -->

<div class="grid grid-cols-1 xl:grid-cols-[minmax(340px,400px)_1fr] gap-4 lg:gap-6 items-start">

  <!-- ============ LEFT: controls + scan inputs ============ -->
  <div class="space-y-4 xl:sticky xl:top-6">

    <!-- Controls -->
    <section class="bg-base-100 border border-base-200 rounded-2xl p-5 shadow-sm space-y-5">
      <div class="space-y-2">
        <div class="text-[10px] font-bold uppercase tracking-[0.22em] text-base-content/50">Store</div>
        <app-store-selector
          [stores]="stores"
          [preSelect]="ctxStoreId"
          [direction]="'horizontal'"
          size="sm"
          (storeSelected)="onStoreSelected($event)">
        </app-store-selector>
      </div>

      <div class="space-y-2">
        <div class="text-[10px] font-bold uppercase tracking-[0.22em] text-base-content/50">Action</div>
        <ui-button-group gap="sm" [fullWidthMobile]="true">
          <ui-button
            [variant]="operation === eStockOperation.Add ? 'success' : 'ghost'"
            size="sm"
            (clicked)="setOperation(eStockOperation.Add)">
            Add to stock
          </ui-button>
          <ui-button
            [variant]="operation === eStockOperation.Remove ? 'danger' : 'ghost'"
            size="sm"
            (clicked)="setOperation(eStockOperation.Remove)">
            Remove
          </ui-button>
        </ui-button-group>
      </div>

      @if (operation === eStockOperation.Add) {
        <div class="space-y-2">
          <div class="text-[10px] font-bold uppercase tracking-[0.22em] text-base-content/50">Expiry</div>
          <div class="flex flex-wrap items-center gap-3">
            <ui-toggle
              [checked]="expiryEnabled"
              color="success"
              label="Apply to scans"
              (checkedChange)="onExpiryToggle($event)">
            </ui-toggle>
            @if (expiryEnabled) {
              <ui-datepicker
                [value]="stickyExpiry"
                placeholder="yyyy-mm-dd"
                size="sm"
                (dateSelected)="onExpirySelected($event)">
              </ui-datepicker>
            }
          </div>
        </div>
      }
    </section>

    <!-- Scan inputs -->
    <section class="bg-base-100 border border-base-200 rounded-2xl p-5 shadow-sm space-y-4">
      @if (!ctxStoreId) {
        <div class="rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm font-medium text-warning">
          Select a store to start scanning.
        </div>
      }

      <!-- Camera -->
      @if (cameraOn) {
        <div class="scan-camera rounded-xl overflow-hidden border border-base-200">
          <app-scanner
            [op]="operation"
            [continuous]="true"
            [sound]="true"
            (codeAccepted)="onCameraScan($event)">
          </app-scanner>
        </div>
      } @else {
        <div class="scan-camera-off rounded-xl border border-dashed border-base-300 flex flex-col items-center justify-center gap-2 text-center px-4 py-8 text-base-content/60">
          <fa-icon [icon]="faCamera" class="text-2xl opacity-60"></fa-icon>
          <div class="text-sm">Camera is off — scan hands-free with your phone/webcam</div>
        </div>
      }

      <div class="flex justify-center">
        <ui-button
          [variant]="cameraOn ? 'success' : 'ghost'"
          [outline]="!cameraOn"
          size="sm"
          [disabled]="!ctxStoreId"
          (clicked)="toggleCamera()">
          <fa-icon [icon]="faCamera" class="mr-1"></fa-icon>
          {{ cameraOn ? 'Stop camera' : 'Use camera' }}
        </ui-button>
      </div>

      <div class="divider my-1 text-xs text-base-content/40">or</div>

      <!-- Keyboard-wedge / manual input -->
      <form (ngSubmit)="onWedgeSubmit()">
        <label class="sr-only" for="wedge">Barcode</label>
        <div class="relative">
          <fa-icon [icon]="faBarcode" class="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"></fa-icon>
          <input
            #wedge
            id="wedge"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            class="input input-bordered w-full ps-10 font-mono tracking-wider tabular-nums"
            placeholder="Scan or type a barcode…"
            [disabled]="!ctxStoreId"
            [(ngModel)]="wedgeInput"
            [ngModelOptions]="{ standalone: true }">
        </div>
        <div class="mt-2 flex items-center justify-between text-xs text-base-content/50">
          <span>Handheld scanner or keyboard — both land here.</span>
          <kbd class="kbd kbd-sm">⏎</kbd>
        </div>
        <button type="submit" class="sr-only" aria-hidden="true">Submit</button>
      </form>
    </section>
  </div>

  <!-- ============ RIGHT: live feed ============ -->
  <section class="space-y-3">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-base-content">This session</h2>
        <div class="mt-1 flex items-center gap-4 text-sm text-base-content/60">
          <span class="inline-flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-success"></span>
            <span class="font-mono font-semibold tabular-nums">{{ addedCount }}</span> added
          </span>
          <span class="inline-flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-error"></span>
            <span class="font-mono font-semibold tabular-nums">{{ removedCount }}</span> removed
          </span>
          <span class="inline-flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full bg-warning"></span>
            <span class="font-mono font-semibold tabular-nums">{{ failedCount }}</span> failed
          </span>
        </div>
      </div>
      @if (feed.length) {
        <ui-button variant="ghost" size="sm" (clicked)="clearFeed()">Clear</ui-button>
      }
    </div>

    @if (feed.length === 0) {
      <div class="rounded-2xl border border-dashed border-base-200 px-4 py-16 text-center text-sm text-base-content/60">
        Nothing scanned yet. Point the camera at a barcode or scan into the field — items land here instantly.
      </div>
    } @else {
      <ul class="bg-base-100 border border-base-200 rounded-2xl shadow-sm divide-y divide-base-200 overflow-hidden">
        @for (row of feed; track trackRow($index, row)) {
          <li
            class="scan-row group flex items-center gap-3 px-4 py-3 border-l-2 transition-colors hover:bg-base-200/50"
            [class.opacity-55]="row.status === 'undone'"
            [style.border-left-color]="row.operation === 'REMOVE' ? 'var(--color-error, transparent)' : 'transparent'">

            <!-- thumbnail -->
            @if (row.thumb) {
              <img [src]="row.thumb" alt="" class="w-11 h-11 rounded-xl object-cover bg-base-200 shrink-0">
            } @else {
              <div
                class="w-11 h-11 rounded-xl shrink-0 grid place-items-center text-white font-extrabold"
                [style.background]="rowColor(row)">
                {{ rowInitial(row) }}
              </div>
            }

            <!-- meta -->
            <div class="min-w-0 flex-1">
              <div class="text-sm font-semibold text-base-content truncate"
                   [class.italic]="!row.name"
                   [class.text-base-content]="!!row.name"
                   [class.line-through]="row.status === 'undone'">
                @if (row.name) {
                  {{ row.name }}
                  @if (row.brand) { <span class="font-normal text-base-content/50">· {{ row.brand }}</span> }
                } @else {
                  Unknown product
                }
              </div>
              <div class="mt-0.5 font-mono text-xs text-base-content/45 tabular-nums truncate">
                {{ row.code }}@if (row.error) { · {{ row.error }} }
              </div>
            </div>

            <!-- status -->
            @switch (row.status) {
              @case ('sending') {
                <span class="inline-flex items-center gap-1.5 text-xs font-semibold text-base-content/50">
                  <span class="loading loading-spinner loading-xs"></span> Sending
                </span>
              }
              @case ('added') { <ui-badge variant="success" size="sm">Added</ui-badge> }
              @case ('removed') { <ui-badge variant="error" size="sm">Removed</ui-badge> }
              @case ('failed') { <ui-badge variant="warning" size="sm">Failed</ui-badge> }
              @case ('undone') { <ui-badge variant="neutral" size="sm">Undone</ui-badge> }
            }

            <!-- actions -->
            <div class="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              @if (row.status === 'added') {
                <ui-button variant="ghost" size="sm" (clicked)="undo(row)">Undo</ui-button>
              }
              @if (row.status === 'failed') {
                <ui-button variant="ghost" size="sm" (clicked)="retry(row)">Retry</ui-button>
              }
            </div>
          </li>
        }
      </ul>
    }
  </section>
</div>
